import React, { useCallback, useEffect, useState } from "react";
import './index.scss';

type LegacyTouchIntroModalProps = {
  modalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
};

const LegacyTouchIntroModal: React.FC<LegacyTouchIntroModalProps> = ({
  // Modal control
  modalOpen,
  onModalOpenChange,
}) => {
  // ----- Modal state (controlled or uncontrolled) -----
  const isModalControlled = modalOpen !== undefined;
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const open = isModalControlled ? (modalOpen as boolean) : internalModalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (isModalControlled) onModalOpenChange?.(next);
      else setInternalModalOpen(next);
    },
    [isModalControlled, onModalOpenChange]
  );

  useEffect(() => {
    // Check if they've seen the intro before
    const hasSeenIntro = localStorage.getItem("hasSeenLegacyTouchIntro") === "true";

    if (!hasSeenIntro) {
      // Open the modal on first visit
      setInternalModalOpen(true);

      // Remember that they've now seen it
      localStorage.setItem("hasSeenLegacyTouchIntro", "true");
    }
  }, []);

  // ESC to close & lock scroll while modal open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, setOpen]);

  return (
    <>
      {/* Modal (with overlay) */}
      {open && (
        <div
          className="lt-intro-modal tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center"
          onClick={(e) => {
            if (e.currentTarget === e.target) setOpen(false); // backdrop click closes
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-container tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-6 tw-relative" style={{ display: "flex", alignItems: "center"}}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
            >
              ✕
            </button>
            <div className="info-container">
              <div className="tw-flex tw-items-start">
                <h2 className="tw-font-semibold tw-text-gray-900">
                  Design your Legacy Touch Ring
                </h2>
              </div>

              <div className="mobile-video-container">
                <video
                  src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/LTBUILDER.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="tw-w-full tw-rounded-lg tw-shadow-lg"
                />
              </div>

              <div
                className="bullet-container tw-text-sm tw-text-gray-700"
              >
                <ol className="modal-bullets">
                  <li className="tw-my-6" style={{ marginLeft: "20px"}}>Choose your ring size.</li>
                  <li className="tw-my-6" style={{ marginLeft: "20px"}}>Customize every detail — or keep the default design.</li>
                  <li className="tw-my-6" style={{ marginLeft: "20px"}}>Tap <b>Add to Cart</b> to proceed with your purchase.</li>
                </ol>
              </div>

              <div className="tw-flex tw-justify-end tw-gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="tw-bg-gray-100 tw-text-gray-800 tw-rounded-lg tw-px-4 tw-py-2 tw-w-full"
                  style={{ backgroundColor: "#002f6c", color: "#fff", borderRadius: "20px" }}
                >
                  Get Started
                </button>
              </div>

              <div className="help-info" style={{ display: "flex", justifyContent: "space-between"}}>
                <div>Need Help?</div>
                <div>
                  <svg className="question-mark" width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1630_58)">
                      <path d="M32 15.9998C32 7.16318 24.8364 0 15.9998 0C7.16318 0 0 7.16318 0 15.9998C0 24.8364 7.16357 31.9996 15.9998 31.9996C24.836 31.9996 32 24.836 32 15.9998ZM30.3321 15.9998C30.3321 23.9151 23.9155 30.3317 15.9998 30.3317C8.08409 30.3317 1.66789 23.9155 1.66789 15.9998C1.66789 8.08409 8.08449 1.66789 15.9998 1.66789C23.9151 1.66789 30.3321 8.08449 30.3321 15.9998Z" fill="#003053"/>
                      <path d="M17.6359 22.5165H14.3641C14.1448 22.5165 13.967 22.6943 13.967 22.9136V26.1854C13.967 26.4048 14.1448 26.5826 14.3641 26.5826H17.6359C17.8553 26.5826 18.0331 26.4048 18.0331 26.1854V22.9136C18.0331 22.6943 17.8553 22.5165 17.6359 22.5165Z" fill="#003053"/>
                      <path d="M9.38346 11.8865C9.38346 12.1073 9.37711 12.6362 9.37711 12.6362C9.37711 12.8546 9.55581 13.0333 9.77422 13.0333H12.6513C12.8697 13.0333 13.0393 12.8546 13.0484 12.6362C13.0552 12.4766 13.2371 9.35167 16.5196 9.35167C17.6093 9.35167 19.1545 10.1106 19.1545 11.8483C19.1545 13.6914 15.7329 15.3807 14.7719 17.1252C14.4443 17.7201 14.2072 18.4472 14.2072 19.2633V20.1814C14.2072 20.3998 14.3859 20.5785 14.6043 20.5785H17.3873C17.6057 20.5785 17.7844 20.3998 17.7844 20.1814V19.407C17.7844 18.7518 18.2475 18.1744 18.6732 17.7638C18.8499 17.593 19.0429 17.4234 19.2462 17.2515C20.7795 15.9541 22.8886 14.5324 22.8886 11.6534C22.8886 4.47826 11.9464 3.7432 9.73888 10.1165C9.56137 10.6292 9.38386 11.3921 9.38386 11.8869L9.38346 11.8865Z" fill="#003053"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_1630_58">
                        <rect width="32" height="32" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                  Call or text (855) 802-6800
                </div>
              </div>
            </div>
            <div className="tw-p-6 desktop-video-container" style={{ flex: "1 1 0px"}}>
              <video
                src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/LTBUILDER.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
                className="tw-w-full tw-rounded-lg tw-shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LegacyTouchIntroModal;
